import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Eye, 
  Users, 
  Clock, 
  TrendingUp,
  ExternalLink,
  Briefcase,
  FileText,
  Award,
  Map as MapIcon,
  ShoppingBag,
  MoreHorizontal,
  Globe,
  Smartphone,
  Calendar,
  Download,
  RefreshCw
} from 'lucide-react';
import {
  getContentAnalytics,
  getGeographicAnalytics,
  getDeviceAnalytics,
  getDailyTrends
} from '../../../lib/siteAnalyticsService';
import { databaseService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import WorldMap from './WorldMap';

export default function ContentAnalytics() {
  const { contentType, contentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [contentData, setContentData] = useState(null);
  
  // Independent Time Filters
  const [visitorFilter, setVisitorFilter] = useState('monthly');
  const [mapFilter, setMapFilter] = useState('monthly');
  const [deviceFilter, setDeviceFilter] = useState('monthly');

  const [analytics, setAnalytics] = useState({}); // Main overview stats
  const [geographicData, setGeographicData] = useState([]);
  const [deviceData, setDeviceData] = useState({});
  const [dailyTrends, setDailyTrends] = useState([]);
  
  const { showToast } = useToast();

  const contentTypeMap = {
    projects: { collection: 'projects', label: 'Project', icon: Briefcase, color: 'text-orange-600', bg: 'bg-orange-50' },
    blogs: { collection: 'blogs', label: 'Blog Post', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
    certificates: { collection: 'certificates', label: 'Certificate', icon: Award, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    maps: { collection: 'spatial_maps', label: 'Map', icon: MapIcon, color: 'text-green-600', bg: 'bg-green-50' },
    products: { collection: 'products', label: 'Product', icon: ShoppingBag, color: 'text-purple-600', bg: 'bg-purple-50' },
    presentations: { collection: 'presentations', label: 'Presentation', icon: FileText, color: 'text-teal-600', bg: 'bg-teal-50' }
  };

  const config = contentTypeMap[contentType];

  // Helper to convert filter to days
  const getDaysFromFilter = (filter) => {
    switch(filter) {
      case 'daily': return 1;
      case 'monthly': return 30;
      case 'total': return 3650;
      default: return 30;
    }
  };

  const FilterToggle = ({ value, onChange }) => (
    <div className="flex bg-gray-100 p-1 rounded-lg">
      {['daily', 'monthly', 'total'].map((type) => (
        <button
          key={type}
          onClick={() => onChange(type)}
          className={`px-3 py-1 text-xs font-medium rounded-md capitalize transition-all ${
            value === type 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {type}
        </button>
      ))}
    </div>
  );

  useEffect(() => {
    if (config) {
        loadPageData();
    }
  }, [contentType, contentId]);

  useEffect(() => {
    loadVisitorData();
  }, [visitorFilter, contentType, contentId]);

  useEffect(() => {
    loadMapData();
  }, [mapFilter, contentType, contentId]);

  useEffect(() => {
    loadDeviceData();
  }, [deviceFilter, contentType, contentId]);

  const loadPageData = async () => {
    setLoading(true);
    try {
        // Load content details
        const docResult = await databaseService.getDocument(config.collection, contentId);
        if (docResult.success) {
            setContentData(docResult.data);
        }

        // Load overview stats (using default 30 days for top summary cards usually)
        const analyticsResult = await getContentAnalytics(contentType, 30);
        if (analyticsResult.success) {
            const specificContent = analyticsResult.data.find(item => item.contentId === contentId);
            setAnalytics(specificContent || {});
        }
        
    } catch (error) {
        console.error("Error loading page data", error);
    } finally {
        setLoading(false);
    }
  };

  const loadVisitorData = async () => {
    const days = getDaysFromFilter(visitorFilter);
    const result = await getDailyTrends(days, contentType, contentId);
    if (result.success) setDailyTrends(result.data);
  };

  const loadMapData = async () => {
    const days = getDaysFromFilter(mapFilter);
    const result = await getGeographicAnalytics(days, contentType, contentId);
    if (result.success) setGeographicData(result.data);
  };

  const loadDeviceData = async () => {
    const days = getDaysFromFilter(deviceFilter);
    const result = await getDeviceAnalytics(days, contentType, contentId);
    if (result.success) setDeviceData(result.data);
  };

  if (!config) return <div className="p-6">Invalid Content Type</div>;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4C1D95]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-4">
          <Link to="/admin/analytics" className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
             <div className={`p-2 rounded-lg ${config.bg}`}>
                <config.icon className={`w-5 h-5 ${config.color}`} />
             </div>
             <div>
                <h1 className="text-xl font-bold text-gray-900 truncate max-w-md">
                    {contentData?.title || contentData?.name || 'Untitled Content'}
                </h1>
                <p className="text-sm text-gray-500 flex items-center gap-2">
                    {config.label} Analytics
                    {contentData?.customSlug && (
                        <a 
                            href={`/${contentType === 'projects' ? 'project' : contentType === 'maps' ? 'map' : contentType === 'blogs' ? 'blog' : contentType === 'certificates' ? 'certificate' : 'product'}/${contentData.customSlug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1 ml-2"
                        >
                            View Live <ExternalLink className="w-3 h-3" />
                        </a>
                    )}
                </p>
             </div>
          </div>
        </div>
      </div>

       {/* Row 1: Metrics Cards */}
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Views Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative overflow-hidden">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Total Views</p>
              <h3 className="text-3xl font-bold text-gray-900">{analytics.views?.toLocaleString() || 0}</h3>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
                <Eye className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Unique Visitors Card */}
        <div className="bg-[#4C1D95] rounded-xl shadow-sm border border-[#4C1D95] p-6 relative overflow-hidden text-white">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Users className="w-24 h-24" />
          </div>
          <div className="flex justify-between items-start mb-4 relative z-10">
            <div>
              <p className="text-xs font-semibold text-purple-200 uppercase tracking-wider mb-1">Unique Visitors</p>
              <h3 className="text-3xl font-bold text-white">{analytics.uniqueVisitors?.toLocaleString() || 0}</h3>
            </div>
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Users className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Avg Time Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative overflow-hidden">
           <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Avg. Time</p>
              <h3 className="text-3xl font-bold text-gray-900">
                {analytics.avgTimeSpent ? `${Math.round(analytics.avgTimeSpent / 1000)}s` : '0s'}
              </h3>
            </div>
            <div className="p-2 bg-orange-50 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Engagement Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative overflow-hidden">
           <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Engagement</p>
              <h3 className="text-3xl font-bold text-gray-900">
                {analytics.views && analytics.uniqueVisitors 
                   ? `${Math.round((analytics.uniqueVisitors / analytics.views) * 100)}%`
                   : '0%'}
              </h3>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
      </div>

       {/* Row 2: Map & Device Stats */}
       <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Countries Map (Left 2/3) */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">Top Countries</h3>
            <FilterToggle value={mapFilter} onChange={setMapFilter} />
          </div>
          
          <div className="flex flex-col md:flex-row h-[350px] gap-6">
             <div className="flex-grow h-full min-h-[250px] relative">
               {geographicData.length > 0 ? (
                  <WorldMap data={geographicData} />
               ) : (
                 <div className="w-full h-full bg-gray-50 rounded-xl flex items-center justify-center text-gray-400">
                    <Globe className="w-12 h-12 mb-2" />
                    <p>No geographic data for {mapFilter}</p>
                 </div>
               )}
             </div>
             
             <div className="w-full md:w-48 flex-shrink-0 space-y-4 overflow-y-auto pr-2 custom-scrollbar">
                {geographicData.slice(0, 6).map((country, idx) => (
                  <div key={idx} className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                       <div className={`w-2 h-2 rounded-full ${['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500'][idx % 4] || 'bg-gray-400'}`}></div>
                       <span className="text-sm font-medium text-gray-600 group-hover:text-gray-900 truncate max-w-[90px]">{country.country}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{country.views}</span>
                  </div>
                ))}
             </div>
          </div>
        </div>

        {/* Device Distribution Donut (Right 1/3) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-bold text-gray-900">Device Types</h3>
            <FilterToggle value={deviceFilter} onChange={setDeviceFilter} />
          </div>
          
          <div className="relative h-64 flex items-center justify-center">
             {(() => {
                const devices = deviceData.devices || [];
                const total = devices.reduce((sum, d) => sum + d.count, 0);
                
                if (total === 0) return <div className="text-gray-400 text-sm">No device data</div>;

                const mobile = devices.find(d => d.name.toLowerCase().includes('mobile'))?.count || 0;
                const mobilePercent = (mobile / total) * 100;
                
                const donutGradient = `conic-gradient(
                  #4C1D95 0% ${mobilePercent}%, 
                  #FCD34D ${mobilePercent}% 100%
                )`;

                return (
                  <div className="relative w-48 h-48 rounded-full" style={{ background: donutGradient }}>
                     <div className="absolute inset-4 bg-white rounded-full flex flex-col items-center justify-center">
                        <span className="text-3xl font-bold text-[#4C1D95]">{Math.round(mobilePercent)}%</span>
                        <span className="text-xs text-gray-500 uppercase font-medium">Mobile</span>
                     </div>
                  </div>
                );
             })()}
          </div>
          
          <div className="mt-8 flex justify-center gap-8">
             <div className="flex items-center gap-2">
                <div className="w-3 h-1 rounded-full bg-[#4C1D95]"></div>
                <span className="text-sm font-medium text-gray-600">Mobile</span>
             </div>
             <div className="flex items-center gap-2">
                <div className="w-3 h-1 rounded-full bg-[#FCD34D]"></div>
                <span className="text-sm font-medium text-gray-600">Desktop</span>
             </div>
          </div>
        </div>
      </div>

       {/* Row 3: Site Visitor Chart */}
       <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
           <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Site Visitor</h3>
              <p className="text-sm text-gray-500">Visitor trends for this content</p>
            </div>
            <div className="flex items-center gap-4">
               <FilterToggle value={visitorFilter} onChange={setVisitorFilter} />
            </div>
          </div>
          
          <div className="h-64 w-full relative">
             <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
               <defs>
                 <linearGradient id="lineGradient2" x1="0" y1="0" x2="0" y2="1">
                   <stop offset="0%" stopColor="#4C1D95" stopOpacity="0.2"/>
                   <stop offset="100%" stopColor="#4C1D95" stopOpacity="0"/>
                 </linearGradient>
               </defs>
               
               {[0, 25, 50, 75, 100].map(y => (
                  <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#f3f4f6" strokeWidth="0.5" />
               ))}
               
               {dailyTrends.length > 0 ? (
                 (() => {
                    const max = Math.max(...dailyTrends.map(d => d.views), 1);
                    const displayData = dailyTrends.slice(-30); 
                    
                    const points = displayData.map((d, i, arr) => {
                       const x = (i / (arr.length - 1)) * 100;
                       const y = 100 - ((d.views / max) * 100);
                       return `${x},${y}`;
                    }).join(' ');
                    
                    const areaPath = `M0,100 ${displayData.map((d, i, arr) => {
                       const x = (i / (arr.length - 1)) * 100;
                       const y = 100 - ((d.views / max) * 100);
                       return `L${x},${y}`;
                    }).join(' ')} L100,100 Z`;
                    
                    if (displayData.length <= 1) {
                         return <text x="50" y="50" textAnchor="middle" fill="#9ca3af" fontSize="4">Not enough trend data</text>;
                    }

                    return (
                       <>
                         <path d={areaPath} fill="url(#lineGradient2)" />
                         <polyline 
                           points={points} 
                           fill="none" 
                           stroke="#4C1D95" 
                           strokeWidth="1.5" 
                           vectorEffect="non-scaling-stroke"
                           strokeLinecap="round" 
                           strokeLinejoin="round" 
                         />
                         {displayData.map((d, i, arr) => (
                            <circle 
                              key={i} 
                              cx={(i / (arr.length - 1)) * 100} 
                              cy={100 - ((d.views / max) * 100)} 
                              r="1.5" 
                              fill="white" 
                              stroke="#4C1D95" 
                              strokeWidth="1" 
                              vectorEffect="non-scaling-stroke"
                            />
                         ))}
                       </>
                    );
                 })()
               ) : (
                  <text x="50" y="50" textAnchor="middle" fill="#9ca3af" fontSize="4">No visitor data for {visitorFilter}</text>
               )}
             </svg>
          </div>
          
           <div className="flex justify-between mt-4 text-xs text-gray-400">
             {dailyTrends.length > 0 && dailyTrends.slice(-14).filter((_, i) => i % 2 === 0).map(d => (
                <span key={d.date}>{new Date(d.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
             ))}
          </div>
       </div>

    </div>
  );
}
